// UPost - 404 Not Found Page
// SEO-friendly 404 page for handling legacy URLs and invalid paths
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Home, Search, BookOpen, PenTool, ArrowRight } from 'lucide-react';

export function NotFound() {
  // Check if this is an old legacy URL
  const currentPath = window.location.pathname;
  const isLegacyUrl = currentPath.includes('.php') || 
                      currentPath.includes('/dealerships/') || 
                      currentPath.includes('/Homeview/');

  return (
    <Layout>
      <Helmet>
        <title>Page Not Found | UPost</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="The page you're looking for doesn't exist. UPost is now an AI-powered writing platform for authors." />
      </Helmet>

      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          {/* 404 Graphic */}
          <div className="mb-8">
            <h1 className="text-9xl font-bold text-purple-200">404</h1>
          </div>

          {/* Main Message */}
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Page Not Found
          </h2>

          {isLegacyUrl ? (
            <div className="mb-8">
              <p className="text-gray-600 mb-4">
                You&apos;ve reached an old page from our previous website.
              </p>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <p className="text-purple-800">
                    <strong>UPost has evolved!</strong> We&apos;re now an AI-powered writing platform 
                    helping authors write novels, screenplays, and more.
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <p className="text-gray-600 mb-8">
              The page you&apos;re looking for doesn&apos;t exist or has been moved.
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/">
              <Button size="lg" className="w-full sm:w-auto">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </Link>
            <Link to="/blog">
              <Button size="lg" variant="outline" className="w-full sm:w-auto">
                <BookOpen className="w-4 h-4 mr-2" />
                Read Writing Tips
              </Button>
            </Link>
          </div>

          {/* Popular Pages */}
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Popular Pages
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Link to="/pricing" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                <ArrowRight className="w-4 h-4 text-purple-600" />
                <span>View Pricing</span>
              </Link>
              <Link to="/screenplay-conversion" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                <ArrowRight className="w-4 h-4 text-purple-600" />
                <span>Novel to Screenplay</span>
              </Link>
              <Link to="/copyright-protection" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                <ArrowRight className="w-4 h-4 text-purple-600" />
                <span>Copyright Protection</span>
              </Link>
              <Link to="/register" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                <ArrowRight className="w-4 h-4 text-purple-600" />
                <span>Start Writing Free</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default NotFound;
